import Cookies from 'js-cookie';
import { create } from 'zustand';

export const COOKIE_SETTINGS_KEY = 'COOKIE_SETTINGS';

/** Consent cookie lifetime in days. Must not be a session cookie — otherwise the banner returns after browser restart / new session (often noticed after deploys). */
export const COOKIE_SETTINGS_MAX_AGE_DAYS = 365;

export enum CookieCategory {
    ANALYTICAL = 'analytical',
    MARKETING = 'marketing',
}

export type CookieSettingsValue = {
    categories: Record<CookieCategory, boolean>;
    consentTimestamp: string | null;
    lastConsentTimestamp: string | null;
};

export const DEFAULT_COOKIE_SETTINGS: CookieSettingsValue = {
    categories: {
        [CookieCategory.ANALYTICAL]: true,
        [CookieCategory.MARKETING]: true,
    },
    consentTimestamp: null,
    lastConsentTimestamp: null,
};

export const getCookieSettings = () => {
    try {
        const settings = Cookies.get(COOKIE_SETTINGS_KEY);
        const parsedSettings = JSON.parse(settings ?? '');
        return {
            categories: {
                analytical:
                    parsedSettings?.categories?.analytical ?? DEFAULT_COOKIE_SETTINGS.categories.analytical,
                marketing:
                    parsedSettings?.categories?.marketing ?? DEFAULT_COOKIE_SETTINGS.categories.marketing,
            },
            consentTimestamp: parsedSettings?.consentTimestamp ?? DEFAULT_COOKIE_SETTINGS.consentTimestamp,
            lastConsentTimestamp:
                parsedSettings?.lastConsentTimestamp ?? DEFAULT_COOKIE_SETTINGS.lastConsentTimestamp,
        };
    } catch {
        return DEFAULT_COOKIE_SETTINGS;
    }
};

export const setCookieSettings = (data: CookieSettingsValue) => {
    Cookies.set(COOKIE_SETTINGS_KEY, JSON.stringify(data), {
        expires: COOKIE_SETTINGS_MAX_AGE_DAYS,
        path: '/',
        secure: true,
        sameSite: 'strict',
    });
};

type StoreValue = {
    cookieSettings: CookieSettingsValue;
    setCookieSettings: (data: CookieSettingsValue) => void;
};

export const useCookieSettings = create<StoreValue>((set) => ({
    cookieSettings: getCookieSettings(),
    setCookieSettings: (data: CookieSettingsValue) => {
        setCookieSettings(data);
        set((store) => ({ ...store, cookieSettings: data }));
    },
}));
